import React from 'react';
import { MapPin } from 'lucide-react';
import { formatMoney } from '../lib/money';

// Normalizes the backend delivery_fee_breakdown (which may be wrapped by
// shipping-size / priority / combine layers) into flat display lines.
const unwrap = (bd, acc) => {
  if (!bd) return;
  if (Array.isArray(bd)) { acc.components = bd; return; }
  if (bd.type === 'pickup') { acc.pickup = true; return; }
  if (bd.type === 'category_pricing') { acc.category = bd; return; }
  if (bd.multiplier && bd.shipping_size) acc.sizeMult = { name: bd.shipping_size.name, mult: bd.multiplier };
  if (bd.priority_multiplier) acc.priorityMult = bd.priority_multiplier;
  if (bd.combine_discount_local) acc.combineDiscount = bd.combine_discount_local;
  unwrap(bd.detail || bd.base, acc);
};

const Line = ({ label, value, testId }) => (
  <div className="flex items-center justify-between text-xs text-slate-500" data-testid={testId}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

export const DeliveryFeeBreakdown = ({ breakdown, distanceKm, currency, total = null, className = '' }) => {
  const acc = {};
  unwrap(breakdown, acc);
  if (acc.pickup) return null;
  const money = (v) => formatMoney(v, currency);
  const hasAny = distanceKm != null || acc.components?.length || acc.category;
  if (!hasAny) return null;

  return (
    <div className={`pl-3 border-l-2 border-slate-100 space-y-0.5 ${className}`} data-testid="delivery-fee-breakdown">
      {distanceKm != null && (
        <Line
          label={<span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> Distance</span>}
          value={`${Number(distanceKm).toFixed(1)} km`}
          testId="fee-line-distance"
        />
      )}
      {(acc.components || []).map((c) => {
        if (c.kind === 'per_km') {
          const qty = Number(c.qty) || 0;
          const line = c.line_local != null ? Number(c.line_local) : Number(c.line_cad) || 0;
          const rate = qty > 0 ? line / qty : Number(c.price_cad) || 0;
          return (
            <Line key={c.key} testId={`fee-line-${c.key}`}
              label={`${c.label || 'Per kilometer'} (${money(rate)}/km × ${qty.toFixed(1)} km)`}
              value={money(line)} />
          );
        }
        return (
          <Line key={c.key} testId={`fee-line-${c.key}`}
            label={c.label || 'Base fee'}
            value={money(c.line_local != null ? c.line_local : c.line_cad)} />
        );
      })}
      {acc.category && (
        <>
          <Line label="Base fee" value={`CA$${Number(acc.category.base_cad || 0).toFixed(2)}`} testId="fee-line-cat-base" />
          <Line label={`Per kilometer (CA$${Number(acc.category.per_km_cad || 0).toFixed(2)}/km × ${Number(acc.category.km || 0).toFixed(1)} km)`}
            value={`CA$${(Number(acc.category.per_km_cad || 0) * Number(acc.category.km || 0)).toFixed(2)}`} testId="fee-line-cat-perkm" />
        </>
      )}
      {acc.sizeMult && <Line label={`Parcel size · ${acc.sizeMult.name}`} value={`× ${Number(acc.sizeMult.mult).toFixed(1)}`} testId="fee-line-size-mult" />}
      {acc.priorityMult && <Line label="Priority delivery" value={`× ${Number(acc.priorityMult).toFixed(1)}`} testId="fee-line-priority" />}
      {acc.combineDiscount ? <Line label="Combined-orders discount" value={`− ${money(acc.combineDiscount)}`} testId="fee-line-combine" /> : null}
      {total != null && (
        <div className="flex items-center justify-between text-xs font-semibold text-slate-700 pt-1 mt-0.5 border-t border-slate-100" data-testid="fee-line-total">
          <span>Total delivery fee</span>
          <span>{money(total)}</span>
        </div>
      )}
    </div>
  );
};

export default DeliveryFeeBreakdown;
